import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { Accordion, AccordionContent, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { getPendingApprovalCount } from "@/services/api/slots";
import { NAV_SECTIONS } from "./nav";
import type { NavItem } from "./nav";

interface SidebarProps {
  collapsed: boolean;
}

const SETTINGS_TITLE = "nav.settings";

/** Live count badge shown next to a nav item (e.g. pending slot approvals). */
function NavBadge({ badgeKey }: { badgeKey: string }) {
  const { data } = useQuery({
    queryKey: [badgeKey, "nav-badge"],
    queryFn: () => getPendingApprovalCount(),
    refetchInterval: 30_000,
    enabled: badgeKey === "slot-approvals",
  });
  const count = badgeKey === "slot-approvals" ? (data?.count ?? 0) : 0;
  if (count <= 0) {
    return null;
  }
  return (
    <Badge
      variant="secondary"
      className="ml-auto h-5 min-w-5 shrink-0 justify-center rounded-full px-1.5 text-[10px] font-semibold"
    >
      {count > 99 ? "99+" : count}
    </Badge>
  );
}

function NavItemLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const { t } = useTranslation();
  const Icon = item.icon;
  const hint = item.hint ? t(item.hint) : t(item.label);
  return (
    <NavLink
      to={item.href}
      end
      title={collapsed ? hint : undefined}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          isActive
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
          collapsed && "justify-center px-2",
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed ? (
        <>
          <span className="truncate">{t(item.label)}</span>
          {item.badgeKey ? <NavBadge badgeKey={item.badgeKey} /> : null}
        </>
      ) : (
        <span className="sr-only">{t(item.label)}</span>
      )}
    </NavLink>
  );
}

/** The Settings section as a collapsible accordion (stays open on submenu). */
function SettingsAccordion({ items, collapsed }: { items: NavItem[]; collapsed: boolean }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [userOpen, setUserOpen] = useState(false);
  const anyActive = items.some((item) => location.pathname === item.href);
  const isOpen = userOpen || anyActive;

  return (
    <Accordion open={isOpen} onOpenChange={setUserOpen}>
      <AccordionTrigger className="rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:bg-muted hover:no-underline hover:text-foreground [&[data-state=open]]:text-foreground">
        {t(SETTINGS_TITLE)}
      </AccordionTrigger>
      <AccordionContent>
        <div className="flex flex-col gap-1 pb-0 pt-1">
          {items.map((item) => (
            <NavItemLink key={item.href} item={item} collapsed={collapsed} />
          ))}
        </div>
      </AccordionContent>
    </Accordion>
  );
}

/** Collapsible left navigation whose entries are filtered by effective permission. */
export function Sidebar({ collapsed }: SidebarProps) {
  const { t } = useTranslation();
  const { any } = usePermissions();

  return (
    <nav className="flex flex-1 flex-col gap-6 overflow-y-auto p-3">
      {NAV_SECTIONS.map((section) => {
        const visible = section.items.filter((item) => any(...item.permissions));
        if (visible.length === 0) {
          return null;
        }
        // When collapsed there is no room for headings or the accordion, so
        // every section renders as a flat icon-only list.
        if (collapsed || section.title !== SETTINGS_TITLE) {
          return (
            <div key={section.title}>
              {!collapsed ? (
                <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t(section.title)}
                </p>
              ) : null}
              <div className="flex flex-col gap-1">
                {visible.map((item) => (
                  <NavItemLink key={item.href} item={item} collapsed={collapsed} />
                ))}
              </div>
            </div>
          );
        }
        return (
          <div key={section.title}>
            <SettingsAccordion items={visible} collapsed={collapsed} />
          </div>
        );
      })}
    </nav>
  );
}
